import { useTheme } from "@mui/material";
import { styled } from "@mui/material";
import { Fragment } from "react";

import { Stack } from "@/Components/v2/atoms/Stack";
import NoData from "@/Components/v2/molecule/NoData";
import { useConfig } from "@/hooks/useConfig";
import BrandEquityGraph from "@/pages/brand-equity/components/BrandEquityGraph/BrandEquityGraph";
import BrandMetricGraph from "@/pages/brand-equity/components/BrandMetricGraph/BrandMetricGraph";
import TotalValues from "@/pages/brand-equity/components/Common/TotalValues";
import { responseHasData } from "@/pages/brand-equity/components/constants";
import {
  getBrandEquityInput,
  getBrandEquityPlaceholderInput,
} from "@/pages/brand-equity/components/graphInput";
import CatchErrorCard from "@/shared/components/CatchErrorCard/CatchErrorCard";

import { useBrandEquityStore } from "../../store/useBrandEquityStore";
import { NewCustomReactTable } from "../table/NewCustomReactTable";

const componentName = "Brand Equity";

const ContentWrapper = styled("div")({
  position: "relative",
  width: "100%",
  height: "100%",
});

const getValue = (obj: any, arrIndexes: any, index: number = 0) => {
  const currentIndex = arrIndexes[index];
  const currentObject = obj[currentIndex];

  if (currentObject == null) {
    return null;
  }

  if (index < arrIndexes.length - 1) {
    return getValue(currentObject, arrIndexes, index + 1);
  } else {
    return currentObject;
  }
};

export const ResponseFilter = (props: any) => {
  const { nestedObjects = ["data"], response, getError } = props.responseFilterProps;

  if (response && response.data && response.data.error) {
    /**
     * the response status code is 200, but it inclues error msg in response.data
     * if response includes error msg
     */
    if (getError === true) {
      return response.data.error;
    }
  } else if (
    getError === undefined &&
    (response == null
      ? false
      : Object.entries(response).length < 1
        ? false
        : response !== null && getValue(response, nestedObjects) !== null)
  ) {
    /**
     * get the data
     */
    return nestedObjects.reduce((k: any, v: any) => (k[v] == null ? null : k[v]), response);
  }
  return [];
};

const BrandEquityChart = ({
  monthAllResponse,
  monthErrors,
  weekAllResponse,
  weekErrors,
}: {
  monthAllResponse: Record<string, any>;
  monthErrors: Record<string, any>;
  weekAllResponse: Record<string, any>;
  weekErrors: Record<string, any>;
}) => {
  const theme = useTheme();
  const {
    config,
    config: { showReturnInsteadOfRevenue },
  } = useConfig();

  const stackProps = {
    border: `1px solid ${theme.palette.section.border}`,
    borderRadius: 4,
    margin: 4,
    marginY: 2,
    padding: 4,
    paddingY: 3,
  };
  const { dateGranularity, isDollarData } = useBrandEquityStore((state) => state.chart);
  const { setDateGranularity, setIsDollarData } = useBrandEquityStore();

  const currencySymbol = showReturnInsteadOfRevenue ? "" : config.currency.getSymbol();
  // For backwards compatibility with the dual response props pattern
  const isMonthlyGranularity = dateGranularity !== "week";
  const currentResponse = isMonthlyGranularity ? monthAllResponse : weekAllResponse;
  const errors = isMonthlyGranularity ? monthErrors : weekErrors;

  const responseKind = currentResponse?.data?.kind;
  const hasData = responseHasData(responseKind);

  const graphInput = (() => {
    if (hasData) {
      const sourceData = ResponseFilter({
        responseFilterProps: {
          nestedObjects: ["data"],
          componentName: componentName,
          response: currentResponse,
          error: errors,
        },
      });

      if (errors !== null) return null;

      return getBrandEquityInput(
        sourceData,
        isDollarData,
        currencySymbol,
        showReturnInsteadOfRevenue
      );
    } else {
      return getBrandEquityPlaceholderInput(currencySymbol);
    }
  })();

  if (graphInput === null) {
    return <CatchErrorCard title={componentName} />;
  }
  const handleDuration = (currSelect: boolean) => {
    if (!currSelect) {
      // Toggle between week and month for backwards compatibility with BrandEquityGraph
      setDateGranularity(dateGranularity === "week" ? "month" : "week");
    }
  };
  const handleIsDollarData = (currSelect: boolean) => {
    if (!currSelect) {
      setIsDollarData(!isDollarData);
    }
  };
  const NO_DATA_DESCRIPTION =
    "Choose a business unit from the options above to view relevant data and insights.";

  if (!hasData) {
    return <NoData title="Select a Business Unit" description={NO_DATA_DESCRIPTION} />;
  }

  return (
    <ContentWrapper>
      <Fragment>
        <Stack {...stackProps}>
          <TotalValues total={graphInput.total} formatCurrency={config.currency.formatCurrency} />
          <BrandEquityGraph
            sourceData={graphInput}
            handleDuration={handleDuration}
            durationSwitch={isMonthlyGranularity}
            handleIsDollarData={handleIsDollarData}
            isDollarData={isDollarData}
            setIsDollarData={setIsDollarData}
            currencySymbol={currencySymbol}
          />
        </Stack>
        <Stack {...stackProps}>
          <BrandMetricGraph
            sourceData={{
              date: graphInput.date,
              brand_metric: graphInput.brand_metric,
            }}
          />
        </Stack>
        <Stack {...stackProps}>
          <NewCustomReactTable
            name={componentName}
            width={`${1500}px`}
            // @ts-ignore
            headers={graphInput.tableHeaders}
            data={graphInput.tableRows}
            currencySign={currencySymbol}
            formatCurrency={config.currency.formatCurrency}
          />
        </Stack>
      </Fragment>
    </ContentWrapper>
  );
};

export default BrandEquityChart;
